import React, { useContext } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { navigate } from 'gatsby';
import ReCAPTCHA from 'react-google-recaptcha';

import SocialLogin from '@common/SocialLogin';
import NProgress from 'nprogress';
import * as ROUTES from '@utils/routes';

import {
  Container,
  FormButton,
  FormSection,
  FormGroup,
  LinkContainer,
  StyledLink,
  PresentMessage,
} from '@styles';
import { FirebaseContext } from '@components/Firebase';

const LoginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Email field is required'),
  password: Yup.string().required('Password field is required'),
});

const Login = () => {
  const { firebase } = useContext(FirebaseContext) || {};
  const recaptchaRef = React.createRef();

  return (
    <FormSection>
      <Container normal>
        <Formik
          initialValues={{
            email: undefined,
            password: undefined,
            recaptcha: undefined,
          }}
          validationSchema={LoginSchema}
          onSubmit={(values, { setSubmitting }) => {
            recaptchaRef.current.execute();

            function login() {
              NProgress.start();
              firebase
                .verifyCaptchaToken(values.recaptcha, 'invis')
                .then(() => {
                  return firebase.doSignInWithEmailAndPassword(
                    values.email,
                    values.password
                  );
                })
                .then(() => {
                  setSubmitting(false);
                  NProgress.done(true);
                  firebase.analytics.logEvent('login', { method: 'password' });
                  navigate(ROUTES.APP_PROFILE);
                })
                .catch((err) => {
                  NProgress.done(true);
                  setSubmitting(false);
                  firebase.analytics.logEvent('exception', {
                    description: err,
                    fatal: false,
                  });
                  if (
                    err.code === 'auth/user-not-found' ||
                    err.code === 'auth/wrong-password'
                  ) {
                    PresentMessage(
                      'error',
                      'Invalid username or password. Please check your credentials and log in again.'
                    );
                  } else {
                    PresentMessage('error', err.message);
                  }
                });
            }

            setTimeout(() => {
              if (values.recaptcha !== undefined) {
                login();
              } else {
                setSubmitting(false);
              }
            }, 500);
          }}
        >
          {({ isSubmitting, isValid, setFieldValue, dirty }) => (
            <Form>
              <FormGroup>
                <label htmlFor="email">Email</label>
                <Field
                  type="email"
                  name="email"
                  placeholder="email@hackersent.com"
                  autoComplete="username"
                />
                <ErrorMessage component="span" name="email" />
              </FormGroup>
              <FormGroup>
                <label htmlFor="password">Password</label>
                <Field
                  type="password"
                  name="password"
                  placeholder="Password"
                  autoComplete="current-password"
                />
                <ErrorMessage component="span" name="password" />
              </FormGroup>
              <LinkContainer>
                <StyledLink to="/forgot-password">
                  I forgot my password
                </StyledLink>
              </LinkContainer>
              <FormButton
                disabled={!isValid || !dirty || isSubmitting}
                type="submit"
              >
                <ReCAPTCHA
                  ref={recaptchaRef}
                  name="recatchpa"
                  size="invisible"
                  sitekey="6LcJZ_YUAAAAACLyt06XiupI-R0Qy-J8GuEJwq83"
                  onExpired={() => {
                    setFieldValue('recaptcha', undefined);
                  }}
                  onChange={(value) => {
                    setFieldValue('recaptcha', value);
                  }}
                />
                Log in
              </FormButton>
            </Form>
          )}
        </Formik>
        <SocialLogin />
      </Container>
    </FormSection>
  );
};

export default Login;
